import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { CalendarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { salesService } from '../../services/salesService';
import {
  CurrencyRupeeIcon,
  ShoppingCartIcon,
  ArchiveBoxIcon,
  UsersIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import DataTable from '../common/DataTable';
import { useNavigate } from 'react-router-dom';
import StatCard from '../common/StatCard';
import { PDFDownloadButton } from './SalesReports';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';

const DatePickerInput = React.forwardRef(({ value, onClick }, ref) => (
  <button
    onClick={onClick}
    ref={ref}
    type="button"
    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600"
  >
    <CalendarIcon className="h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
    {value || 'Select date'}
  </button>
));

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    activeCustomers: 0,
    pendingPayments: 0
  });
  const [sales, setSales] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [filteredSales, setFilteredSales] = useState([]);
  const [totalSalesPeriod, setTotalSalesPeriod] = useState('month');
  const [totalOrdersPeriod, setTotalOrdersPeriod] = useState('month');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [uniqueCustomers, setUniqueCustomers] = useState([]);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [reportsDateRange, setReportsDateRange] = useState({
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const salesData = await salesService.getAllSales();
        setSales(salesData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (sales.length > 0) {
      calculatePeriodStats('month', 'sales');
      calculatePeriodStats('month', 'orders');
    }
  }, [sales]);

  useEffect(() => {
    if (sales.length > 0) {
      // Extract unique customer names from sales
      const customers = [...new Set(sales
        .filter(sale => sale && sale.customerName)
        .map(sale => sale.customerName))]
        .filter(Boolean)
        .sort();
      setUniqueCustomers(customers);
    }
  }, [sales]);

  useEffect(() => {
    if (!dateRange.startDate && !dateRange.endDate) {
      setFilteredSales(sales);
      return;
    }

    const filtered = sales.filter(sale => {
      if (!sale.finalizedAt) return false;
      const saleDate = new Date(sale.finalizedAt);
      // Reset time part for accurate date comparison
      saleDate.setHours(0, 0, 0, 0);

      if (dateRange.startDate && dateRange.endDate) {
        const start = new Date(dateRange.startDate);
        const end = new Date(dateRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return saleDate >= start && saleDate <= end;
      }

      if (dateRange.startDate) {
        const start = new Date(dateRange.startDate);
        start.setHours(0, 0, 0, 0);
        return saleDate >= start;
      }

      if (dateRange.endDate) {
        const end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
        return saleDate <= end;
      }

      return true;
    });

    setFilteredSales(filtered);
  }, [dateRange, sales]);

  useEffect(() => {
    const fetchBusinessDetails = async () => {
      try {
        const docRef = doc(db, 'settings', 'business');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBusinessDetails(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching business details:', error);
      }
    };
    fetchBusinessDetails();
  }, []);

  const calculatePeriodStats = (period, statType) => {
    const setPeriod = statType === 'sales' ? setTotalSalesPeriod : setTotalOrdersPeriod;
    setPeriod(period);

    const now = new Date();
    let startDate = new Date();

    switch (period) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      default:
        break;
    }

    const periodSales = sales.filter(sale => {
      const saleDate = new Date(sale.finalizedAt);
      return saleDate >= startDate && saleDate <= now && sale.status === 'completed';
    });

    // Calculate period-specific stats
    const totalSales = periodSales.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
    const totalOrders = periodSales.length;

    // Calculate overall stats (independent of period)
    const completedSales = sales.filter(sale => sale.status === 'completed');
    const overallTotalSales = completedSales.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
    const overallTotalOrders = completedSales.length;
    const averageOrderValue = overallTotalOrders > 0 ? overallTotalSales / overallTotalOrders : 0;

    // Calculate pending payments from all sales
    const pendingPayments = sales.reduce((sum, sale) => {
      if (sale.status === 'completed') {
        const pending = (sale.totalAmount || 0) - (sale.amountReceived || 0);
        return sum + (pending > 0 ? pending : 0);
      }
      return sum;
    }, 0);

    setStats(prev => ({
      ...prev,
      ...(statType === 'sales' ? { totalSales } : { totalOrders }),
      averageOrderValue,
      pendingPayments
    }));
  };

  const columns = [
    {
      header: 'Date',
      accessorKey: 'finalizedAt',
      cell: (info) => {
        const date = info.getValue();
        if (!date) return 'Pending';

        // Format date as dd/mm/yyyy
        const formattedDate = new Date(date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });

        return formattedDate;
      },
    },
    {
      header: 'Customer',
      accessorKey: 'customerName',
    },
    {
      header: 'Items',
      accessorKey: 'items',
      cell: (info) => {
        const items = info.getValue();
        return (
          <div className="max-w-md">
            <ul className="space-y-1">
              {items?.map((item, index) => (
                <li key={index} className="text-sm flex flex-wrap gap-1">
                  <span className="font-medium">{item.vegetable}</span>
                  <span>
                    {item.estimatedQuantity}kg
                    {item.actualQuantity && ` (${item.actualQuantity}kg)`}
                  </span>
                  {item.unitPrice && (
                    <span className="text-gray-600">₹{item.unitPrice}/kg</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      },
    },
    {
      header: 'Total',
      accessorKey: 'totalAmount',
      cell: (info) => {
        const value = info.getValue();
        return value !== undefined ? `₹${value.toLocaleString()}` : '-';
      },
    },
    {
      header: 'Received',
      accessorKey: 'amountReceived',
      cell: (info) => {
        const value = info.getValue();
        return value !== undefined ? `₹${value.toLocaleString()}` : '-';
      },
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: (info) => (
        <div className="flex space-x-2">
          {info.row.original.status === 'completed' && (

            <button
              onClick={() => navigate(`/sales/order/${info.row.original.id}`)}
              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                text-primary-700 bg-primary-100 hover:bg-primary-200 
                dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              View Details
            </button>
          )}
        </div>
      ),
    },
  ];

  const stats_cards = [
    {
      title: 'Total Sales',
      value: `₹${stats.totalSales.toLocaleString()}`,
      icon: CurrencyRupeeIcon,
      periodType: 'sales',
      currentPeriod: totalSalesPeriod,
      textColor: 'dark:text-white',
      showPeriodToggle: true
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders.toString(),
      icon: ShoppingCartIcon,
      periodType: 'orders',
      currentPeriod: totalOrdersPeriod,
      textColor: 'dark:text-white',
      showPeriodToggle: true
    },
    {
      title: 'Average Order Value',
      value: `₹${stats.averageOrderValue.toFixed(2)}`,
      icon: ArchiveBoxIcon,
      iconBgColor: 'bg-yellow-500',
      borderColor: 'border-yellow-500',
      hoverBgColor: 'hover:bg-yellow-500',
      textColor: 'text-yellow-500',
      showPeriodToggle: false
    },
    {
      title: 'Pending Payments',
      value: `₹${stats.pendingPayments.toLocaleString()}`,
      icon: UsersIcon,
      iconBgColor: 'bg-red-500',
      borderColor: 'border-red-500',
      hoverBgColor: 'hover:bg-red-500',
      textColor: 'text-red-500',
      showPeriodToggle: false
    }
  ];

  const renderReportsSection = () => (
    <div className="mb-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-lg font-medium mb-4 dark:text-white">Generate Reports</h2>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <select
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
          >
            <option value="">All Customers</option>
            {uniqueCustomers.map(customer => (
              <option key={customer} value={customer}>{customer}</option>
            ))}
          </select>
        </div>

        <DatePicker
          selectsRange={true}
          startDate={reportsDateRange.startDate}
          endDate={reportsDateRange.endDate}
          onChange={(update) => setReportsDateRange({ startDate: update[0], endDate: update[1] })}
          customInput={<DatePickerInput />}
          isClearable
          dateFormat="MM/dd/yyyy"
          maxDate={new Date()}
          minDate={new Date(2020, 0, 1)}
          showDisabledMonthNavigation
        />

        <PDFDownloadButton
          orders={filteredSales}
          dateRange={reportsDateRange}
          businessDetails={businessDetails}
          customerFilter={selectedCustomer}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats_cards.map((stat, index) => (
          <StatCard
            key={index}
            {...stat}
            onPeriodChange={calculatePeriodStats}
          />
        ))}
      </div>

      {renderReportsSection()}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div className="relative rounded-md flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Search sales..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <DatePicker
                selectsRange={true}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(update) => {
                  setDateRange({ startDate: update[0], endDate: update[1] });
                }}
                customInput={<DatePickerInput />}
                dateFormat="dd/MM/yyyy"
                isClearable
                placeholderText="Select date range"
                maxDate={new Date()}
                minDate={new Date(2020, 0, 1)}
                showDisabledMonthNavigation
              />
              {(dateRange.startDate || dateRange.endDate) && (
                <button
                  onClick={() => setDateRange({ startDate: null, endDate: null })}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Clear filter
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : (
            <DataTable
              data={filteredSales}
              columns={columns}
              sorting={sorting}
              setSorting={setSorting}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              showSearch={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}